/**
 * useVideoJsVideo — drives the branded player's native <video> through
 * video.js + VHS. Kept dependency-light at the call site: same signature as a
 * plain source-attach hook, so the branded player keeps its custom chrome,
 * sponsor overlays, and useVideoControls on top (video.js runs with
 * controls:false — we only use it for its HLS/VHS engine and quality control).
 *
 * Source selection (per active clip): prefer the HLS manifest (clip.hls,
 * shipped on every branded clip) so we get adaptive bitrate + programmatic
 * quality control; fall back to progressive MP4.
 *
 * Quality policy mirrors the shared U.GG/OP.GG VideoPlayer
 * (src/MVVM/VideoPlayer/index.tsx): renditions >= 1080p only play in
 * fullscreen, and on entering fullscreen we seed `tech.vhs.bandwidth` high so
 * ABR considers the top rendition immediately instead of ramping from cold.
 *
 * Returns the player ref. Consumers MUST drive playback (events, play/pause,
 * seek, mute) through this player instance, never through the original
 * <video> ref: on iOS video.js clones the element it's handed
 * (Html5.movingMediaElementInDOM is false there) and plays through the clone,
 * so listeners and commands on the React-rendered element silently no-op.
 *
 * Fullscreen upgrade strategy — HARD RESTART. With 6–12s HLS segments, a soft
 * in-place switch (flipping enabled renditions) keeps playing the already-
 * buffered low-quality media and only upgrades at the next segment boundary,
 * which reads as "stayed blurry, then jumped." Instead, on fullscreen we gate
 * every sub-1080 rendition OFF entirely and reload the stream so VHS can ONLY
 * pick HD, seeking back to where the viewer was. video.js renders its own
 * loading spinner over the reload, so the upgrade reads as a deliberate
 * "loading HD" beat and the first frame after the reload is already 1080p.
 *
 * video.js + videojs-contrib-quality-levels are already project deps (used by
 * the shared VideoPlayer), so this adds no packages.
 */
import { RefObject, useEffect, useRef } from "react";
import videojs from "video.js";
import "videojs-contrib-quality-levels";
import "video.js/dist/video-js.css";

/** Renditions at/above this height only play in fullscreen (mirrors U.GG). */
const FULLSCREEN_ONLY_MIN_HEIGHT = 1080;

/**
 * Bandwidth (bps) seeded on entering fullscreen so VHS's ABR picks the top
 * rendition immediately. Identical to the shared player's `tech.vhs.bandwidth
 * = 50_000_000`. VHS adapts back down if the real connection can't sustain it.
 */
const FULLSCREEN_BANDWIDTH_SEED = 50_000_000;

interface VideoJsArgs {
  hlsUrl?: string;
  mp4Url?: string;
  /** Lifts the windowed 1080p gate so VHS can use the HD rendition. */
  isFullscreen: boolean;
  /**
   * Changes when the source should swap — typically the active clip id.
   * videoRef is a stable RefObject, so this drives the per-clip source swap
   * on the single long-lived player instance (no dispose/recreate per clip).
   */
  reattachKey?: unknown;
}

/** Minimal shape of the VHS tech we touch (videojs-http-streaming). */
interface VhsTech {
  vhs?: {
    bandwidth: number;
  };
}

export const useVideoJsVideo = (
  videoRef: RefObject<HTMLVideoElement | null>,
  { hlsUrl, mp4Url, isFullscreen, reattachKey }: VideoJsArgs,
): RefObject<videojs.Player | null> => {
  const playerRef = useRef<videojs.Player | null>(null);

  // Latest HLS url, read inside the fullscreen effect (which only depends on
  // isFullscreen) so the pinned reload always targets the current clip
  // without making the effect re-run on clip change.
  const hlsUrlRef = useRef(hlsUrl);
  hlsUrlRef.current = hlsUrl;

  // When true, the quality-level gate enables ONLY >=1080 renditions (the
  // hard HD-only mode used in fullscreen). When false, ONLY sub-1080 are
  // enabled (windowed). Read inside the addqualitylevel listener so a
  // post-reload manifest gets gated correctly the moment levels appear.
  const hdOnlyRef = useRef(false);

  // Create the player once the <video> exists; dispose on unmount (separate
  // effect below). Kept separate from the source-swap effect so clip changes
  // don't tear down the VHS tech. reattachKey is in the deps because the
  // parent gates the <video> on having a source — if the element mounts on a
  // later commit, this effect must run again to adopt it (the playerRef
  // guard makes any extra re-runs no-ops).
  useEffect(() => {
    const video = videoRef.current;
    if (!video || playerRef.current) return undefined;

    // video.js warns if the element lacks the video-js class; add it so it
    // adopts our existing <video> as the html5 tech rather than replacing it.
    video.classList.add("video-js");

    const player = videojs(video, {
      controls: false, // custom PlayerChrome stays on top
      autoplay: "muted",
      muted: true,
      playsinline: true,
      preload: "auto",
      fill: true, // fill PlayerFrame instead of forcing an aspect ratio
      sources: [],
    });
    playerRef.current = player;

    // Apply the HD/windowed gate whenever a quality level is added. Levels
    // populate asynchronously as VHS parses the manifest (and again after the
    // fullscreen reload), so re-gating on every addqualitylevel keeps a fresh
    // manifest constrained from its first segment.
    const qualityLevels = player.qualityLevels();
    const updateQualityLevels = () => {
      // HD-only (fullscreen): enable ONLY >=1080. Windowed: enable ONLY
      // sub-1080 (HD gated off).
      const hdOnly = hdOnlyRef.current;
      for (let i = 0; i < qualityLevels.length; i++) {
        const level = qualityLevels[i];
        level.enabled =
          level.height >= FULLSCREEN_ONLY_MIN_HEIGHT ? hdOnly : !hdOnly;
      }
    };
    qualityLevels.on("addqualitylevel", updateQualityLevels);

    return undefined;
  }, [videoRef, reattachKey]);

  // Dispose on unmount ONLY — never on clip change. dispose() also tears
  // down the qualityLevels plugin and every player listener, so the
  // addqualitylevel gate above doesn't need a manual off().
  useEffect(
    () => () => {
      const player = playerRef.current;
      if (player && !player.isDisposed()) player.dispose();
      playerRef.current = null;
    },
    [],
  );

  // Swap source + restart on clip change. HLS preferred for quality control;
  // MP4 is the universal fallback.
  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;

    if (hlsUrl) {
      player.src({ src: hlsUrl, type: "application/x-mpegURL" });
    } else if (mp4Url) {
      player.src({ src: mp4Url, type: "video/mp4" });
    } else {
      return;
    }

    player.currentTime(0);
    const playPromise = player.play();
    if (playPromise && typeof playPromise.catch === "function") {
      playPromise.catch(() => {
        /* autoplay may be blocked pre-gesture; first interaction resumes */
      });
    }
  }, [hlsUrl, mp4Url, reattachKey]);

  // Fullscreen quality swap via a hard restart (see file header):
  //   - Fullscreen → hdOnly=true (gate disables every sub-1080 rendition),
  //     reload the HLS source, seek to the saved time, resume. VHS rebuilds
  //     its buffer using only the HD rendition, so the first frame after the
  //     reload is 1080p; video.js shows its own spinner over the reload.
  //   - Windowed  → hdOnly=false (re-enable the sub-1080 ladder, gate HD
  //     off). No reload — ABR drifts down naturally.
  useEffect(() => {
    const player = playerRef.current;
    if (!player) return;

    const applyGate = () => {
      const qualityLevels = player.qualityLevels();
      const hdOnly = hdOnlyRef.current;
      for (let i = 0; i < qualityLevels.length; i++) {
        const level = qualityLevels[i];
        level.enabled =
          level.height >= FULLSCREEN_ONLY_MIN_HEIGHT ? hdOnly : !hdOnly;
      }
    };

    if (isFullscreen) {
      const url = hlsUrlRef.current;
      // No HLS source → nothing to upgrade (MP4 fallback has no ladder).
      if (!url) return;

      const t = player.currentTime() ?? 0;
      hdOnlyRef.current = true;

      // Constrain any already-parsed levels immediately, then reload. On the
      // fresh manifest the addqualitylevel gate re-applies hdOnly so VHS
      // never even considers a sub-1080 rendition.
      applyGate();
      player.src({ src: url, type: "application/x-mpegURL" });
      player.load();
      player.one("loadedmetadata", () => {
        const tech = player.tech({ IWillNotUseThisInPlugins: true }) as
          | VhsTech
          | undefined;
        if (tech?.vhs) tech.vhs.bandwidth = FULLSCREEN_BANDWIDTH_SEED;
        applyGate();
        player.currentTime(t);
        const p = player.play();
        if (p && typeof p.catch === "function") p.catch(() => undefined);
      });
    } else {
      hdOnlyRef.current = false;
      // Re-enable the sub-1080 ladder in place; no reload — ABR downshifts.
      applyGate();
    }
  }, [isFullscreen]);

  return playerRef;
};
